import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, inject, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatAngularModule } from '../../../modules/mat-angular.module';
import { BulkSearchService } from '../../../../services/bulk-search.service';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TabFlow } from '../services/template-common.service';
import { GenericTemplateCommonService } from '../services/generic-template-common.service';
import { GlobalLoaderService } from '../../../global-loader/global-loader.service';

// Import existing tab components
import { TableColumn } from '../../table/table.component';
import { CitationBadge } from '../../citation-badge/citation-badge.component';
import { GenericAiContentIndexComponent } from '../../../tabs-generic/generic-ai-content-index/generic-ai-content-index.component';
import { GenericAiCitationsComponent } from '../../../tabs-generic/generic-ai-citations/generic-ai-citations.component';
import { GenericCategoryAnalysisTabComponent } from '../../../tabs-generic/generic-category-analysis-tab/generic-category-analysis-tab.component';
import { GenericPromptsComponent } from '../../../tabs-generic/generic-prompts/generic-prompts.component';
import { GenericOverviewComponent } from '../../../tabs-generic/generic-overview/generic-overview.component';
import { GenericAiVisibilityComponent } from '../../generic-ai-visibility/generic-ai-visibility.component';
import { GenericSentimentTrackingComponent } from '../../../tabs-generic/generic-sentiment-tracking/generic-sentiment-tracking.component';
import { GenericPerformanceTrackingComponent } from '../../../tabs-generic/generic-performance-tracking/generic-performance-tracking.component';
import { GenericContentCreationComponent } from '../../../tabs-generic/generic-content-creation/generic-content-creation.component';
import { GenericConsumerInsightsComponent } from '../../../tabs-generic/generic-consumer-insights/generic-consumer-insights.component';
import { GenericEeatAuditComponent } from '../../../tabs-generic/generic-eeat-audit/generic-eeat-audit.component';
import { GenericLandingPageAuditComponent } from '../../../tabs-generic/generic-landing-page-audit/generic-landing-page-audit.component';
import { GenericAIAuditComponent } from '../../../tabs-generic/generic-ai-audit/generic-ai-audit.component';
import { GenericPromptResearchComponent } from '../../../tabs-generic/generic-prompt-research/generic-prompt-research.component';
import { GenericPromptTrackingComponent } from '../../../tabs-generic/generic-prompt-tracking/generic-prompt-tracking.component';
import { GenericGlossaryComponent } from '../../../tabs-generic/generic-glossary/generic-glossary.component';
import { SourceChip } from '../../source-chips/source-chips.component';
import { GlobalLoaderComponent } from '../../../global-loader/global-loader.component';
import { CommonCircleLoaderComponent } from '../../common-circle-loader/common-circle-loader.component';

// Import organized models
import { TabConfig, TabsConfiguration, TabData, TabState } from '../../../models/generic-models';

// Re-export for backward compatibility
export type { TabConfig, TabsConfiguration, TabData, TabState };

@Component({
  selector: 'app-generic-common-tabs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAngularModule,

    // Tab Components
    GenericAiContentIndexComponent,
    GenericAiCitationsComponent,
    GenericSentimentTrackingComponent,
    GenericCategoryAnalysisTabComponent,
    GenericPromptsComponent,
    GenericPerformanceTrackingComponent,
    GenericOverviewComponent,
    GenericAiVisibilityComponent,
    GenericConsumerInsightsComponent,
    GenericContentCreationComponent,
    GenericEeatAuditComponent,
    GenericLandingPageAuditComponent,
    GenericAIAuditComponent,
    GenericPromptResearchComponent,
    GenericPromptTrackingComponent,
    GenericGlossaryComponent,
    GlobalLoaderComponent,
    CommonCircleLoaderComponent,
  ],
  templateUrl: './generic-common-tabs.component.html',
  styleUrl: './generic-common-tabs.component.scss'
})
export class GenericCommonTabsComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() config!: TabsConfiguration;
  @Input() selectedEntities: any[] = [];
  @Input() category: string = 'Generic';
  @Input() country: string = 'UK';
  /** When true, show circle loader in tab content area (e.g. while loading GET /generic/history/{id}) */
  @Input() historyLoading = false;

  @Output() tabChange = new EventEmitter<string>();
  @Output() reloadTab = new EventEmitter<string>();
  @Output() visualizationChange = new EventEmitter<{ tabId: string, type: string }>();

  // Tab state
  activeTab: string | null = null;
  activeParentTab: string | null = null; // Track parent tab for nested structure
  expandedParentTabs: Set<string> = new Set(); // Track which parent tabs are expanded
  isLoading = false;
  error: string | null = null;
  
  // Bulk search progress tracking
  bulkSearchProgress: any = null;
  backgroundTabsProgress: { loaded: number; total: number; currentTab?: string; taskId?: string } | null = null;
  tabData: TabData | null | any = null;
  
  // Global loader state
  showGlobalLoader = false;
  currentTaskId: string | null = null;
  showBackgroundButton = false;
  showSkipAuditsButton = false;
  
  // Re-Audit state
  reAuditLoading = false;
  
  // ViewChild references to audit components
  @ViewChild('eeatAuditCmp') eeatAuditCmp?: GenericEeatAuditComponent;
  @ViewChild('landingPageAuditCmp') landingPageAuditCmp?: GenericLandingPageAuditComponent;
  @ViewChild('aiAuditCmp') aiAuditCmp?: GenericAIAuditComponent;

  // Service subscriptions
  private serviceSubscriptions: Subscription[] = [];
  private currentTabFlow: TabFlow = { type: 'generic', activeTab: 'generic-ai-citations' };

  // Data observables for sharing between components
  private dataSubject = new BehaviorSubject<TabData | null>(null);
  public data$: Observable<TabData | null> = this.dataSubject.asObservable();

  private destroy$ = new Subject<void>();

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);
  private bulkSearchService = inject(BulkSearchService);
  projectId: string | null = null;

  constructor(
    private genericTemplateCommonService: GenericTemplateCommonService,
    private globalLoaderService: GlobalLoaderService
  ) { }
  
  /**
   * Handle background load button click
   */
  onBackgroundLoadClick(): void {
    // Show snackbar notification
    this.snackBar.open(
      'Analysis started in background. You will be notified when complete.',
      'Close',
      { duration: 5000 }
    );
    
    // Navigate to project page if projectId is available
    if (this.projectId) {
      this.router.navigate(['/university/projects']).catch(() => {
        // Fallback to admin projects if university route doesn't exist
        this.router.navigate(['/admin/projects']);
      });
    } else {
      // Fallback: navigate to projects page
      this.router.navigate(['/university/projects']).catch(() => {
        this.router.navigate(['/admin/projects']);
      });
    }
  }
  
  /**
   * Handle skip audits button click
   */
  onSkipAuditsClick(): void {
    if (!this.currentTaskId) {
      console.error('No task ID available to skip audits');
      return;
    }
    
    this.bulkSearchService.skipAuditsForTask(this.currentTaskId).subscribe({
      next: (response: any) => {
        // Update total tabs from 11 to 8 (removing 3 audit tabs)
        const currentState = this.globalLoaderService.getCurrentState();
        if (currentState.totalTabs === 11) {
          // Filter out audit tabs from completed tab names
          const auditTabs = ['EEAT Audit', 'Landing Page Audit', 'AI Audit', 'eeat-audit', 'landing-page-audit', 'ai-audit'];
          const filteredCompletedTabs = currentState.completedTabNames.filter(
            tab => !auditTabs.some(audit => tab.toLowerCase().includes(audit.toLowerCase()))
          );
          
          // Recalculate completed tabs count (excluding audit tabs)
          const auditTabsCompleted = currentState.completedTabNames.length - filteredCompletedTabs.length;
          const newCompletedTabs = Math.max(0, currentState.completedTabs - auditTabsCompleted);
          
          // Update to 9 tabs (removing 3 audit tabs)
          this.globalLoaderService.updateProgress(
            currentState.progressPercentage,
            newCompletedTabs,
            filteredCompletedTabs,
            currentState.currentTab,
            currentState.estimatedTimeRemaining,
            9 // New total tabs count
          );
        }
        
        // Hide skip audits button
        this.showSkipAuditsButton = false;
        
        this.snackBar.open(
          'Audits skipped. You can re-run them later.',
          'Close',
          { duration: 5000 }
        );
      },
      error: (error: any) => {
        console.error('Error skipping audits:', error);
        this.snackBar.open(
          'Failed to skip audits. Please try again.',
          'Close',
          { duration: 5000 }
        );
      }
    });
  }
  
  /**
   * Handle cancel button click
   */
  onCancelClick(): void {
    if (!this.currentTaskId) {
      console.error('No task ID available to cancel');
      return;
    }
    
    const taskIdToCancel = this.currentTaskId; // Store before resetting
    
    // Stop polling immediately to prevent further status checks
    this.bulkSearchService.stopProgressPolling();
    
    // Reset state immediately
    this.currentTaskId = null;
    this.showSkipAuditsButton = false;
    
    // Hide the loader immediately
    this.showGlobalLoader = false;
    
    // Call cancel API
    this.bulkSearchService.cancelTask(taskIdToCancel).subscribe({
      next: (response: any) => {
        console.log('✅ Task cancelled successfully:', response);
        
        this.snackBar.open('Analysis cancelled.', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top'
        });
        
        // Reset global loader
        this.globalLoaderService.resetLoader();
        
        // Navigate to project page
        if (this.projectId) {
          // Navigate to project and reload to reset state
          const currentUrl = this.router.url;
          const projectUrl = `/common-ai-analysis/generic/${this.projectId}`;
          
          if (currentUrl === projectUrl) {
            // Already on the project page, just reload
            window.location.reload();
          } else {
            // Navigate to project page
            this.router.navigate([projectUrl]).then(() => {
              // Reload after navigation to ensure clean state
              window.location.reload();
            });
          }
        } else {
          // Fallback to projects page
          this.router.navigate(['/university/projects']).catch(() => {
            this.router.navigate(['/admin/projects']);
          });
        }
      },
      error: (error: any) => {
        console.error('❌ Error cancelling task:', error);
        this.snackBar.open('Failed to cancel analysis. Please try again.', 'Close', { duration: 5000 });
        // Note: We already stopped polling and reset state, so don't revert
      }
    });
  }

  ngOnInit(): void {
    // Get projectId from route params
    this.route.params.subscribe(params => {
      this.projectId = params['projectId'] || null;
    });
    
    this.initializeTabs();
    this.setupDataObservables();
    this.genericTemplateCommonService.requestOpenTabWithScroll$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ tabId, scrollToId }) => {
        this.openTab(tabId, true);
        if (scrollToId) {
          setTimeout(() => {
            document.getElementById(scrollToId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 450);
        }
      });
  }

  ngAfterViewInit(): void {
    this.openTab(this.config.defaultTab);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Unsubscribe from service subscriptions
    this.serviceSubscriptions.forEach(sub => sub.unsubscribe());
  }

  private initializeTabs(): void {
    if (this.config && this.config.defaultTab) {
      // Check if default tab is a sub-tab
      const parentTab = this.config.tabs.find(tab => 
        tab.subTabs && tab.subTabs.some(subTab => subTab.id === this.config.defaultTab)
      );
      
      if (parentTab) {
        this.activeParentTab = parentTab.id;
        this.expandedParentTabs.add(parentTab.id);
      }
      
      this.activeTab = this.config.defaultTab;
    }
  }


  private setupDataObservables(): void {
    // Update observables when data changes
    this.data$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      console.log('Common Tabs: Data received:', data);
      this.tabData = data;
    });

    // Subscribe to tab-specific responses based on active tab
    this.genericTemplateCommonService.analysisResponse$.pipe(takeUntil(this.destroy$)).subscribe(response => {
      if ((this.activeTab === 'overview' || this.activeTab === 'prompts') && response) {
        console.log('Common Tabs: Overview response received:', response);
        this.tabData = response;
      }
    });

    this.genericTemplateCommonService.aiCitationsResponse$.pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (this.activeTab === 'ai-citations' && response) {
        console.log('Common Tabs: AI Citations response received:', response);
        this.tabData = response;
      }
    });

    this.genericTemplateCommonService.categoryAnalysisResponse$.pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (this.activeTab === 'category-analysis' && response) {
        console.log('Common Tabs: Category Analysis response received:', response);
        this.tabData = response;
      }
    });

    this.genericTemplateCommonService.sentimentTrackingResponse$.pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (this.activeTab === 'sentiment-tracking' && response) {
        this.tabData = response;
      }
    });

    this.genericTemplateCommonService.consumerInsightsResponse$.pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (this.activeTab === 'consumer-insights' && response) {
        this.tabData = response;
      }
    });

    this.genericTemplateCommonService.aiContentIndexResponse$.pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (this.activeTab === 'ai-content-index' && response) {
        this.tabData = response;
      }
    });

    this.genericTemplateCommonService.contentCreationResponse$.pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (this.activeTab === 'content-creation' && response) {
        this.tabData = response;
      }
    });

    this.genericTemplateCommonService.performanceTrackingResponse$.pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (this.activeTab === 'performance-tracking' && response) {
        this.tabData = response;
      }
    });

    this.genericTemplateCommonService.eeatAuditResponse$.pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (this.activeTab === 'eeat-audit' && response) {
        this.tabData = response;
      }
    });

    this.genericTemplateCommonService.landingPageAuditResponse$.pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (this.activeTab === 'landing-page-audit' && response) {
        this.tabData = response;
      }
    });

    // Subscribe to loading state from service
    this.genericTemplateCommonService.isLoading$.pipe(takeUntil(this.destroy$)).subscribe(loading => {
      this.isLoading = loading;
    });

    // Subscribe to error state from service
    this.genericTemplateCommonService.error$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      this.error = error;
    });

    // Subscribe to bulk search progress from service
    this.genericTemplateCommonService.bulkSearchProgress$.pipe(takeUntil(this.destroy$)).subscribe(progress => {
      this.bulkSearchProgress = progress;
      console.log('Common Tabs: Bulk search progress update:', progress);
    });
    this.genericTemplateCommonService.backgroundTabsProgress$.pipe(takeUntil(this.destroy$)).subscribe(progress => {
      this.backgroundTabsProgress = progress;
    });

    // Subscribe to global loader state to show/hide loader during bulk search
    this.globalLoaderService.loaderState$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      this.showGlobalLoader = state.showProgress;
      console.log('Common Tabs: Global loader state:', state.showProgress);
      
      // Show background button when loader is shown (for progress mode)
      if (state.showProgress) {
        setTimeout(() => {
          this.showBackgroundButton = true;
        }, 2500);
      } else {
        this.showBackgroundButton = false;
      }
    });
    
    // Subscribe to current task ID
    this.genericTemplateCommonService.currentTaskId$.pipe(takeUntil(this.destroy$)).subscribe(taskId => {
      this.currentTaskId = taskId;
      // Skip audits button is no longer needed since audits are removed from bulk search
      this.showSkipAuditsButton = false;
    });
    
    // Show background button when loader is shown (for non-progress mode)
    this.genericTemplateCommonService.isLoading$.pipe(takeUntil(this.destroy$)).subscribe(loading => {
      if (loading && !this.showGlobalLoader) {
        // Show background button after delay for standard loader
        setTimeout(() => {
          this.showBackgroundButton = true;
        }, 2500);
      } else if (!this.showGlobalLoader) {
        this.showBackgroundButton = false;
      }
    });
  }

  getVisibleTabs(): TabConfig[] {
    if (!this.config || !this.config.tabs) return [];
    
    // Filter tabs based on visibility conditions
    return this.config.tabs.filter(tab => {
      // If tab has subTabs, always show it (parent tab)
      if (tab.subTabs && tab.subTabs.length > 0) {
        return true;
      }
      
      // Landing page audit tab only visible if URL is detected in project description
      // But now it's a sub-tab, so check within parent tab
      if (tab.id === 'landing-page-audit') {
        const projectData = this.genericTemplateCommonService.getCurrentProjectData();
        const description = projectData?.description || '';
        return this.hasUrlInDescription(description);
      }
      return true;
    });
  }
  
  /**
   * Get sub-tabs for a parent tab
   */
  getSubTabs(parentTabId: string): TabConfig[] {
    const parentTab = this.config?.tabs.find(tab => tab.id === parentTabId);
    if (!parentTab || !parentTab.subTabs) return [];
    
    // Filter sub-tabs based on visibility conditions
    return parentTab.subTabs.filter(subTab => {
      // Landing page audit tab only visible if URL is detected in project description
      if (subTab.id === 'landing-page-audit') {
        const projectData = this.genericTemplateCommonService.getCurrentProjectData();
        const description = projectData?.description || '';
        return this.hasUrlInDescription(description);
      }
      return true;
    });
  }
  
  /**
   * Check if a tab is a parent tab (has sub-tabs)
   */
  isParentTab(tabId: string): boolean {
    const tab = this.config?.tabs.find(t => t.id === tabId);
    return tab ? !!(tab.subTabs && tab.subTabs.length > 0) : false;
  }
  
  /**
   * Toggle parent tab expansion
   */
  toggleParentTab(tabId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    if (this.expandedParentTabs.has(tabId)) {
      this.expandedParentTabs.delete(tabId);
    } else {
      this.expandedParentTabs.add(tabId);
    }
  }
  
  /**
   * Check if parent tab is expanded
   */
  isParentTabExpanded(tabId: string): boolean {
    return this.expandedParentTabs.has(tabId);
  }
  
  /**
   * Open sub-tab (child of parent tab)
   */
  openSubTab(parentTabId: string, subTabId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    this.activeParentTab = parentTabId;
    this.openTab(subTabId);
  }
  
  /**
   * Get sub-menu template reference (for mobile menu)
   */
  getSubMenu(parentTabId: string): any {
    // This will be handled by template reference
    return null;
  }

  /**
   * Check if project description contains a URL
   */
  private hasUrlInDescription(description: string | null | undefined): boolean {
    if (!description) return false;
    // Pattern 1: Full URLs (http:// or https://)
    const urlPattern = /https?:\/\/[^\s\)\]\>\"\']+/;
    if (urlPattern.test(description)) return true;
    // Pattern 2: Domain patterns (www.example.com or example.com)
    const domainPattern = /(?:www\.)?[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+/;
    return domainPattern.test(description);
  }

  openTab(tabId: string = 'overview', skipAnalysis: boolean = false): void {
    console.log(`🔄 Opening tab: ${tabId}, skipAnalysis: ${skipAnalysis}`);
    
    // Check if this is a parent tab - if so, expand it and don't switch content
    if (this.isParentTab(tabId)) {
      this.toggleParentTab(tabId);
      // If no sub-tab is active, activate the first sub-tab
      const subTabs = this.getSubTabs(tabId);
      if (subTabs.length > 0 && !this.activeTab) {
        this.activeParentTab = tabId;
        this.openTab(subTabs[0].id, skipAnalysis);
      }
      return;
    }
    
    // Check if this tab belongs to a parent tab
    const parentTab = this.config?.tabs.find(tab => 
      tab.subTabs && tab.subTabs.some(subTab => subTab.id === tabId)
    );
    if (parentTab) {
      this.activeParentTab = parentTab.id;
      // Expand parent tab
      if (!this.expandedParentTabs.has(parentTab.id)) {
        this.expandedParentTabs.add(parentTab.id);
      }
    } else {
      this.activeParentTab = null;
    }
    
    // Force component recreation by resetting activeTab first
    const previousTab = this.activeTab;
    // Reset current tab payload so new-tab empty state/loader is computed correctly.
    this.tabData = null;
    this.activeTab = null;
    
    // Small delay to ensure component destruction
    setTimeout(() => {
      this.activeTab = tabId;
      this.tabChange.emit(tabId);

      // Update tab flow in service
      this.currentTabFlow.activeTab = tabId;
      this.genericTemplateCommonService.updateTabFlow(this.currentTabFlow);
      
      // Tab switch is UI-only; do not trigger analysis APIs here.
      // Data refresh should happen only from explicit analyze/reload actions.

      console.log(`Common Tabs: Opening tab ${tabId} - component will auto-load data`);
    }, 10);
  }

  /** Show reload button when the current tab has reloadable: true in config. */
  get showCategoryAnalysisReload(): boolean {
    if (!this.activeTab || !this.config?.tabs) return false;
    const tab = this.config.tabs.find((t: TabConfig) => t.id === this.activeTab)
      ?? this.config.tabs.flatMap((t: TabConfig) => t.subTabs || []).find((st: TabConfig) => st.id === this.activeTab);
    return !!tab?.reloadable;
  }

  reloadCurrentTab(): void {
    if (this.activeTab) {
      // Map tab IDs to their corresponding analysis types for bulk-search single-tab reload
      const tabMapping: { [key: string]: string } = {
        'overview': 'overview',
        'ai-citations': 'ai-citations',
        'category-analysis': 'category-analysis',
        'prompts': 'overview',
        'ai-visibility': 'ai-visibility',
        'sentiment-tracking': 'sentiment-tracking',
        'ai-content-index': 'ai-content-index',
        'performance-tracking': 'performance-tracking',
        'content-creation': 'content-creation',
        'consumer-insights': 'consumer-insights'
      };

      const analysisType = tabMapping[this.activeTab];
      if (analysisType) {
        this.genericTemplateCommonService.triggerTabReload(analysisType, true);
      } else {
        this.reloadTab.emit(this.activeTab);
      }
    }
  }

  private updateTabData(data: TabData | null | any): void {
    this.tabData = data;
    this.dataSubject.next(data);
    // this.cdr.detectChanges();
  }



  // Helper methods for UI
  /** LLM comparison data for AI Visibility tab (from overview). */
  getAiVisibilityComparisonData(): any[] {
    const res = this.genericTemplateCommonService.getCurrentAnalysisResponse?.();
    return res?.llm_comparison?.comparison_data ?? [];
  }

  getTabTitle(tabId: string): string {
    // Check if it's a sub-tab
    for (const parentTab of this.config.tabs) {
      if (parentTab.subTabs) {
        const subTab = parentTab.subTabs.find(st => st.id === tabId);
        if (subTab) {
          return subTab.name;
        }
      }
    }
    
    const tab = this.config.tabs.find(t => t.id === tabId);
    return tab ? tab.name : tabId;
  }

  getActiveTabIcon(): string {
    if (!this.activeTab) return 'dashboard';
    
    // Check if it's a sub-tab
    for (const parentTab of this.config.tabs) {
      if (parentTab.subTabs) {
        const subTab = parentTab.subTabs.find(st => st.id === this.activeTab);
        if (subTab) {
          return subTab.icon;
        }
      }
    }
    
    const tab = this.config.tabs.find(t => t.id === this.activeTab);
    return tab ? tab.icon : 'dashboard';
  }

  getLoaderTitle(): string {
    // If bulk search is in progress, show bulk analysis message
    if (this.bulkSearchProgress) {
      return 'Preparing your comprehensive brand analysis dashboard. This may take a few moments as we process and analyze your data using advanced AI computations. Thank you for your patience—your results will be ready soon!';
    }
    
    // Individual tab loading messages
    const tabTitles = {
      'overview': 'Preparing your dashboard. This may take few minutes while we run extensive AI computations.',
      'ai-citations': 'Preparing your AI citations analysis. This may take few minutes while we run extensive AI computations.',
      'category-analysis': 'Preparing your category analysis. This may take few minutes while we run extensive AI computations.',
      'sentiment-tracking': 'Preparing your sentiment analysis. This may take few minutes while we run extensive AI computations.',
      'consumer-insights': 'Preparing your consumer insights analysis. This may take few minutes while we run extensive AI computations.',
      'ai-content-index': 'Preparing your AI content index analysis. This may take few minutes while we run extensive AI computations.',
      'content-creation': 'Preparing your content creation analysis. This may take few minutes while we run extensive AI computations.',
      'performance-tracking': 'Preparing your performance tracking analysis. This may take few minutes while we run extensive AI computations.',
      'eeat-audit': 'Preparing your EEAT Audit analysis. This may take few minutes while we run extensive AI computations.',
      'landing-page-audit': 'Preparing your landing page audit analysis. This may take few minutes while we run extensive AI computations.',
      'ai-audit': 'Preparing your AI audit analysis. This may take few minutes while we run extensive AI computations.'
    };
    return tabTitles[this.activeTab as keyof typeof tabTitles] || 'Preparing your analysis. This may take few minutes while we run extensive AI computations.';
  }

  shouldShowGlobalLoader(): boolean {
    if (this.isLoading) return true;
    // Keep global loader only for initial/overview phase; background tabs should not blank current tab UI.
    const currentTab = (this.bulkSearchProgress?.current_tab || '').toLowerCase();
    return this.showGlobalLoader && (this.activeTab === 'overview' || currentTab.includes('overview'));
  }

  shouldShowInTabLoader(): boolean {
    return !this.isLoading && !this.shouldShowGlobalLoader() && !this.error && !!this.activeTab && !this.hasDataForActiveTab() && !!this.backgroundTabsProgress;
  }

  getInTabLoaderMessage(): string {
    if (!this.backgroundTabsProgress) return 'Preparing data...';
    const loaded = typeof this.backgroundTabsProgress.loaded === 'number' ? this.backgroundTabsProgress.loaded : 0;
    const total = typeof this.backgroundTabsProgress.total === 'number' ? this.backgroundTabsProgress.total : 0;
    const current = this.backgroundTabsProgress.currentTab || 'Current section';
    return `Background analysis in progress (${loaded}/${total} tabs). Processing ${current}.`;
  }

  hasDataForActiveTab(): boolean {
    if (!this.activeTab) return false;
    // Self-loading tabs fetch their own API data on component init.
    if (this.activeTab === 'prompt-research' || this.activeTab === 'prompt-tracking' || this.activeTab === 'glossary') {
      return true;
    }
    const existingData = this.genericTemplateCommonService.getExistingResponseForTab(this.activeTab);
    return !!existingData;
  }

  getFailureMessage(): string {
    if (this.error) {
      return this.error;
    }
    
    const tabNames = {
      'overview': 'dashboard',
      'ai-citations': 'AI citations analysis',
      'category-analysis': 'category analysis',
      'sentiment-tracking': 'sentiment analysis',
      'consumer-insights': 'consumer insights analysis',
      'ai-content-index': 'AI content index analysis',
      'content-creation': 'content creation analysis',
        'performance-tracking': 'performance tracking analysis',
        'eeat-audit': 'EEAT audit analysis'
      };
    
    const tabName = tabNames[this.activeTab as keyof typeof tabNames] || 'analysis';
    return `Unable to load ${tabName} data. This could be due to network issues, insufficient data, or processing errors.`;
  }

  private getCurrentResponseForActiveTab(): any {
    if (!this.activeTab) return null;

    let response = null;
    switch (this.activeTab) {
      case 'overview':
        response = this.genericTemplateCommonService.getCurrentAnalysisResponse();
        break;
      case 'ai-citations':
        response = this.genericTemplateCommonService.getCurrentAiCitationsResponse();
        break;
      case 'category-analysis':
        response = this.genericTemplateCommonService.getCurrentCategoryAnalysisResponse();
        break;
      case 'sentiment-tracking':
        response = this.genericTemplateCommonService.getCurrentSentimentTrackingResponse();
        break;
      case 'consumer-insights':
        response = this.genericTemplateCommonService.getCurrentConsumerInsightsResponse();
        break;
      case 'ai-content-index':
        response = this.genericTemplateCommonService.getCurrentAiContentIndexResponse();
        break;
      case 'content-creation':
        response = this.genericTemplateCommonService.getCurrentContentCreationResponse();
        break;
      case 'performance-tracking':
        response = this.genericTemplateCommonService.getCurrentPerformanceTrackingResponse();
        break;
      case 'eeat-audit':
        response = this.genericTemplateCommonService.getCurrentEeatAuditResponse();
        break;
      default:
        response = null;
    }
    
    console.log(`Common Tabs: Getting current response for ${this.activeTab}:`, response);
    return response;
  }

  getEntityNames(): string[] {
    return this.selectedEntities.map(entity => entity.name || entity);
  }

  // Method to update data from parent component
  updateData(data: TabData): void {
    this.updateTabData(data);
  }

  // Method to update error state from parent component
  updateErrorState(error: string | null): void {
    this.error = error;
  }
  
  /**
   * Check if current tab is an audit tab (including sub-tabs)
   */
  isAuditTab(): boolean {
    return this.activeTab === 'eeat-audit' || 
           this.activeTab === 'landing-page-audit' || 
           this.activeTab === 'ai-audit' ||
           this.activeParentTab === 'audit';
  }
  
  /**
   * Check if Re-Audit is currently loading
   */
  isReAuditLoading(): boolean {
    if (!this.isAuditTab()) return false;
    
    switch (this.activeTab) {
      case 'eeat-audit':
        return (this.eeatAuditCmp as any)?.reAuditLoading || this.eeatAuditCmp?.loading || false;
      case 'landing-page-audit':
        return (this.landingPageAuditCmp as any)?.reAuditLoading || this.landingPageAuditCmp?.loading || false;
      case 'ai-audit':
        return (this.aiAuditCmp as any)?.reAuditLoading || this.aiAuditCmp?.loading || false;
      default:
        return false;
    }
  }
  
  /**
   * Trigger Re-Audit for the current audit tab
   */
  triggerReAudit(): void {
    if (!this.isAuditTab()) return;
    
    switch (this.activeTab) {
      case 'eeat-audit':
        this.eeatAuditCmp?.reAudit();
        break;
      case 'landing-page-audit':
        this.landingPageAuditCmp?.reAudit();
        break;
      case 'ai-audit':
        this.aiAuditCmp?.reAudit();
        break;
    }
  }
}
